'use client'

import { useTranslation } from 'react-i18next'
import { tiers } from '../../constants/subscriptionTiers'

function discountLabel(tier) {
  const p = parseFloat(String(tier.price.yearly).replace(/[^0-9.]/g, ''))
  const o = parseFloat(String(tier.originalPrice?.yearly ?? '').replace(/[^0-9.]/g, ''))
  if (!isNaN(p) && !isNaN(o) && o > 0 && p < o) {
    return `−${Math.round((1 - p / o) * 100)}%`
  }
  return null
}

export default function Pricing() {
  const { t } = useTranslation()

  return (
    <section id="pricing" className="bg-surface px-6 py-24 sm:py-28 lg:px-16">
      <div className="mx-auto flex max-w-[1152px] flex-col gap-14">
        <div className="flex flex-col items-center gap-3.5 text-center">
          <h2 className="text-4xl font-bold tracking-[-1px] text-ink">{t("pricingTitle")}</h2>
          <p className="max-w-[480px] text-base text-muted">{t("pricingText")}</p>
        </div>

        <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {tiers.map((tier) => {
            const name = t(`tiers.${tier.slug}.name`)
            const description = t(`tiers.${tier.slug}.description`)
            const features = t(`tiers.${tier.slug}.features`, { returnObjects: true })
            const discount = discountLabel(tier)
            const oldPrice = tier.originalPrice?.yearly
            const popular = tier.mostPopular
            return (
              <div
                key={tier.id}
                className={
                  'relative flex flex-col gap-5 rounded-4xl bg-cream p-7 ' +
                  (popular
                    ? 'border-2 border-smg_orange shadow-[0_14px_36px_rgba(255,107,1,0.14)]'
                    : 'border-2 border-transparent')
                }
              >
                {popular && (
                  <span className="ds-cta absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-3.5 py-1.5 text-[11px] tracking-[1px] shadow-orange">
                    {t("mostPopular")}
                  </span>
                )}

                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-bold text-ink">{name}</h3>
                  <p className="text-[13px] leading-snug text-muted">{description}</p>
                </div>

                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    {oldPrice && (
                      <span className="text-sm font-medium text-faint line-through">{oldPrice}</span>
                    )}
                    {discount && (
                      <span className="rounded-full bg-[#E4EFE0] px-2 py-0.5 text-[11px] font-bold text-[#4A7A54]">
                        {discount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[34px] font-bold tracking-[-0.5px] text-ink">
                      {tier.price.yearly}
                    </span>
                    <span className="text-[13px] font-medium text-muted">{t("yearSuffix")}</span>
                  </div>
                </div>

                <a
                  href={`/pricing/${tier.slug}?frequency=yearly`}
                  aria-describedby={tier.id}
                  className={
                    'flex h-[46px] items-center justify-center rounded-full text-sm font-bold transition-all duration-150 hover:-translate-y-0.5 ' +
                    (popular
                      ? 'ds-cta'
                      : 'border-[1.5px] border-border_soft bg-surface text-ink hover:brightness-105')
                  }
                >
                  {t('pricingCTA')}
                </a>

                <div className="flex flex-col gap-2.5">
                  {(Array.isArray(features) ? features : []).map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-[13.5px] font-medium text-ink_soft">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                        <path d="M5 12.5 L10 17.5 L19 7" stroke="#7BAE85" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {feature}
                    </div>
                  ))}
                </div>

                <p className="mt-auto pt-1 text-[11px] text-faint">
                  {tier.slug === 'teacher' ? t("plan1Comment") : t("plan1Comment2")}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
